import { useState } from 'react';
import { apiClient } from '@/services/apiClient';
import { stageFile } from './useStagingUpload';
import {
  validateAssetBatch,
  normalizeAssetBatch,
} from '../utils/assetValidation';
import type { AssetUpload } from './usePhotoUpload';

/**
 * Orchestrates the documented two-step local-file upload:
 *
 *   1. For each picked file, POST `impact/staging-upload` and capture
 *      `{fileUrl, filename}`. Failures are surfaced per-file so the
 *      user can drop the bad one and retry the rest.
 *   2. POST `impact/upload` once with every successfully-staged asset.
 *      The server responds with `data.success[]` / `data.failed[]`.
 *
 * Spec: docs/asset-management-upload-photo-sources.md
 */

interface UploadProgress {
  total: number;
  staged: number;
  failedStaging: number;
  uploaded: number;
  failedUpload: number;
  isStaging: boolean;
  isUploading: boolean;
}

const initialProgress: UploadProgress = {
  total: 0,
  staged: 0,
  failedStaging: 0,
  uploaded: 0,
  failedUpload: 0,
  isStaging: false,
  isUploading: false,
};

/**
 * Per-file input to `runLocalPhotoUpload`. `asset` carries the metadata
 * the user filled in for that picture (title, program, village, etc.);
 * `file` is the raw `File` object to be staged.
 */
export interface LocalUploadEntry {
  /** Client-generated UUID. Reused as the staging key and the upload `id`. */
  id: string;
  file: File;
  asset: Omit<AssetUpload, 'fileUrl' | 'filename' | 'source'> & {
    /** Canonical RGF filename built on the client; sent on impact/upload. */
    filename: string;
  };
}

export interface LocalUploadStagingFailure {
  id: string;
  filename: string;
  error: string;
}

export interface LocalUploadResult {
  /** Server `data.success[]` — successfully created assets. */
  success: Array<{ id: string; bf_asset_id: string }>;
  /** Combined server `data.failed[]` plus client staging failures. */
  failed: Array<{ id: string; error: string }>;
}

interface UploadResponseEnvelope {
  message: string;
  data: {
    success: Array<{ id: string; bf_asset_id: string }>;
    failed: Array<{ id: string; error: string }>;
  };
}

const UPLOAD_ENDPOINT = '/console/asset_management/impact/upload';

export const useLocalPhotoUpload = () => {
  const [progress, setProgress] = useState<UploadProgress>(initialProgress);
  const [error, setError] = useState<string | null>(null);
  const [stagingFailures, setStagingFailures] = useState<LocalUploadStagingFailure[]>([]);

  const reset = () => {
    setProgress(initialProgress);
    setError(null);
    setStagingFailures([]);
  };

  /**
   * Run both steps. Resolves with the merged success / failure result. On a
   * total failure (no assets staged, or impact/upload rejected wholesale)
   * resolves with `null` and `error` is set.
   */
  const runUpload = async (entries: LocalUploadEntry[]): Promise<LocalUploadResult | null> => {
    if (entries.length === 0) {
      setError('No photos selected.');
      return null;
    }

    setError(null);
    setStagingFailures([]);
    setProgress({
      total: entries.length,
      staged: 0,
      failedStaging: 0,
      uploaded: 0,
      failedUpload: 0,
      isStaging: true,
      isUploading: false,
    });

    // Stage each file. Run in parallel — the server is the bottleneck,
    // and a sequential loop would serialise unnecessarily. `Promise.all`
    // here doesn't reject because we wrap each entry in a try/catch.
    const staged: Array<LocalUploadEntry & { fileUrl: string }> = [];
    const failures: LocalUploadStagingFailure[] = [];

    await Promise.all(
      entries.map(async (entry) => {
        try {
          const result = await stageFile(entry.file);
          staged.push({ ...entry, fileUrl: result.fileUrl });
          setProgress((p) => ({ ...p, staged: p.staged + 1 }));
        } catch (err: any) {
          failures.push({
            id: entry.id,
            filename: entry.asset.filename,
            error: err?.message || 'Staging failed.',
          });
          setProgress((p) => ({ ...p, failedStaging: p.failedStaging + 1 }));
        }
      }),
    );

    setStagingFailures(failures);

    if (staged.length === 0) {
      setProgress((p) => ({ ...p, isStaging: false }));
      setError(
        failures.length > 0
          ? `Could not stage any photos: ${failures[0].error}`
          : 'Could not stage any photos. Please try again.',
      );
      return { success: [], failed: failures.map((f) => ({ id: f.id, error: f.error })) };
    }

    // Build the final assets payload. `source: 'Local'` per spec; `filename`
    // is the client-built canonical name; `fileUrl` comes from staging.
    const rawAssets: AssetUpload[] = staged.map((entry) => ({
      ...entry.asset,
      id: entry.id,
      source: 'Local',
      type: entry.asset.type ?? 'Image',
      fileUrl: entry.fileUrl,
      filename: entry.asset.filename,
    }));

    const validation = validateAssetBatch(rawAssets);
    if (!validation.isValid) {
      setProgress((p) => ({ ...p, isStaging: false }));
      setError(`Validation failed: ${validation.errors.join('; ')}`);
      return {
        success: [],
        failed: [
          ...failures.map((f) => ({ id: f.id, error: f.error })),
          ...rawAssets.map((a) => ({ id: a.id, error: 'Client validation failed.' })),
        ],
      };
    }

    const assets = normalizeAssetBatch(rawAssets);

    setProgress((p) => ({ ...p, isStaging: false, isUploading: true }));

    try {
      const { data } = await apiClient.post<UploadResponseEnvelope>(UPLOAD_ENDPOINT, {
        assets,
      });
      const result = data?.data ?? { success: [], failed: [] };
      setProgress((p) => ({
        ...p,
        isUploading: false,
        uploaded: result.success.length,
        failedUpload: result.failed.length,
      }));
      return {
        success: result.success,
        failed: [
          ...failures.map((f) => ({ id: f.id, error: f.error })),
          ...result.failed,
        ],
      };
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Upload failed.';
      setProgress((p) => ({
        ...p,
        isUploading: false,
        failedUpload: assets.length,
      }));
      setError(message);
      return {
        success: [],
        failed: [
          ...failures.map((f) => ({ id: f.id, error: f.error })),
          ...assets.map((a) => ({ id: a.id, error: message })),
        ],
      };
    }
  };

  return {
    runUpload,
    reset,
    progress,
    error,
    stagingFailures,
  };
};
